import { useReducer, useCallback } from 'react';
import type {
  ChatMessage,
  BotResponse,
  SendMessageParams,
  EditMessageTextParams,
  EditMessageReplyMarkupParams,
  DeleteMessageParams,
  AnswerCallbackQueryParams,
  ConnectionConfig,
  Update,
} from '../types/telegram';
import {
  createChatMessage,
  createMessageUpdate,
  createCallbackQueryUpdate,
  resetCounters,
  getNextMessageId,
} from '../lib/updates';

// ============================================
// State
// ============================================

interface ChatState {
  messages: ChatMessage[];
  pendingCallbackAlert: { text: string; showAlert: boolean } | null;
}

const initialState: ChatState = {
  messages: [],
  pendingCallbackAlert: null,
};

// ============================================
// Actions
// ============================================

type ChatAction =
  | { type: 'ADD_USER_MESSAGE'; payload: { text: string } }
  | { type: 'ADD_BOT_MESSAGE'; payload: ChatMessage }
  | {
      type: 'EDIT_MESSAGE';
      payload: { messageId: number; text: string; reply_markup?: ChatMessage['reply_markup'] };
    }
  | {
      type: 'EDIT_MESSAGE_MARKUP';
      payload: { messageId: number; reply_markup?: ChatMessage['reply_markup'] };
    }
  | { type: 'DELETE_MESSAGE'; payload: { messageId: number } }
  | { type: 'SET_CALLBACK_ALERT'; payload: { text: string; showAlert: boolean } | null }
  | { type: 'CLEAR_MESSAGES' };

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'ADD_USER_MESSAGE': {
      const message = createChatMessage(action.payload.text, 'outgoing');
      return { ...state, messages: [...state.messages, message] };
    }
    case 'ADD_BOT_MESSAGE': {
      return { ...state, messages: [...state.messages, action.payload] };
    }
    case 'EDIT_MESSAGE': {
      return {
        ...state,
        messages: state.messages.map((msg): ChatMessage => {
          if (msg.message_id !== action.payload.messageId) {
            return msg;
          }
          const updated: ChatMessage = {
            ...msg,
            text: action.payload.text,
            edit_date: Math.floor(Date.now() / 1000),
          };
          if (action.payload.reply_markup !== undefined) {
            updated.reply_markup = action.payload.reply_markup;
          }
          return updated;
        }),
      };
    }
    case 'EDIT_MESSAGE_MARKUP': {
      return {
        ...state,
        messages: state.messages.map((msg): ChatMessage => {
          if (msg.message_id !== action.payload.messageId) {
            return msg;
          }
          const updated: ChatMessage = {
            ...msg,
            edit_date: Math.floor(Date.now() / 1000),
          };
          if (action.payload.reply_markup !== undefined) {
            updated.reply_markup = action.payload.reply_markup;
          }
          return updated;
        }),
      };
    }
    case 'DELETE_MESSAGE': {
      return {
        ...state,
        messages: state.messages.filter((msg) => msg.message_id !== action.payload.messageId),
      };
    }
    case 'SET_CALLBACK_ALERT': {
      return { ...state, pendingCallbackAlert: action.payload };
    }
    case 'CLEAR_MESSAGES': {
      return initialState;
    }
    default:
      return state;
  }
}

// ============================================
// Hook
// ============================================

interface UseChatOptions {
  config: ConnectionConfig | null;
  onError: (message: string) => void;
}

interface UseChatReturn {
  messages: ChatMessage[];
  pendingCallbackAlert: { text: string; showAlert: boolean } | null;
  sendMessage: (text: string) => Promise<void>;
  sendCallbackQuery: (data: string, message: ChatMessage) => Promise<void>;
  handleBotResponse: (response: BotResponse) => void;
  clearMessages: () => void;
  dismissCallbackAlert: () => void;
}

export function useChat(options: UseChatOptions): UseChatReturn {
  const { config, onError } = options;
  const [state, dispatch] = useReducer(chatReducer, initialState);

  const sendUpdate = useCallback(
    async (update: Update): Promise<void> => {
      if (!config) {
        onError('Not connected');
        return;
      }

      const url = new URL(config.webhookUrl);
      url.searchParams.set('sessionId', config.sessionId);

      try {
        const response = await fetch(url.toString(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(update),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status.toString()}`);
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        onError(`Failed to send update: ${message}`);
      }
    },
    [config, onError]
  );

  const sendMessage = useCallback(
    async (text: string): Promise<void> => {
      dispatch({ type: 'ADD_USER_MESSAGE', payload: { text } });
      const update = createMessageUpdate(text);
      await sendUpdate(update);
    },
    [sendUpdate]
  );

  const sendCallbackQuery = useCallback(
    async (data: string, message: ChatMessage): Promise<void> => {
      const update = createCallbackQueryUpdate(data, message);
      await sendUpdate(update);
    },
    [sendUpdate]
  );

  const handleBotResponse = useCallback((response: BotResponse): void => {
    switch (response.method) {
      case 'sendMessage': {
        const params = response.params as SendMessageParams;
        const messageId = getNextMessageId();
        const message: ChatMessage = {
          message_id: messageId,
          date: Math.floor(Date.now() / 1000),
          chat: { id: params.chat_id, type: 'private' },
          text: params.text,
          direction: 'incoming',
        };
        if (params.reply_markup !== undefined) {
          message.reply_markup = params.reply_markup;
        }
        dispatch({ type: 'ADD_BOT_MESSAGE', payload: message });
        break;
      }
      case 'editMessageText': {
        const params = response.params as EditMessageTextParams;
        dispatch({
          type: 'EDIT_MESSAGE',
          payload: {
            messageId: params.message_id,
            text: params.text,
            reply_markup: params.reply_markup,
          },
        });
        break;
      }
      case 'editMessageReplyMarkup': {
        const params = response.params as EditMessageReplyMarkupParams;
        dispatch({
          type: 'EDIT_MESSAGE_MARKUP',
          payload: {
            messageId: params.message_id,
            reply_markup: params.reply_markup,
          },
        });
        break;
      }
      case 'deleteMessage': {
        const params = response.params as DeleteMessageParams;
        dispatch({ type: 'DELETE_MESSAGE', payload: { messageId: params.message_id } });
        break;
      }
      case 'answerCallbackQuery': {
        const params = response.params as AnswerCallbackQueryParams;
        if (params.text !== undefined && params.text !== '') {
          dispatch({
            type: 'SET_CALLBACK_ALERT',
            payload: { text: params.text, showAlert: params.show_alert ?? false },
          });
        }
        break;
      }
    }
  }, []);

  const clearMessages = useCallback(() => {
    dispatch({ type: 'CLEAR_MESSAGES' });
    resetCounters();
  }, []);

  const dismissCallbackAlert = useCallback(() => {
    dispatch({ type: 'SET_CALLBACK_ALERT', payload: null });
  }, []);

  return {
    messages: state.messages,
    pendingCallbackAlert: state.pendingCallbackAlert,
    sendMessage,
    sendCallbackQuery,
    handleBotResponse,
    clearMessages,
    dismissCallbackAlert,
  };
}
